/**
 * Verkline Cart Drawer JS - v2 with Toast Notification
 *
 * 2026-04-26 changes:
 * - Cart icon click no longer opens drawer; navigates natively to /cart
 * - Add-to-cart no longer auto-opens drawer; shows compact toast notification
 *   (bottom-right, 3s, product name + "View cart" link, 3 languages PL/DE/EN)
 * - Drawer code retained but inactive (can be opened programmatically if needed)
 *
 * Effect: 100% customers proceeding to checkout pass through /cart page where
 * B2B EU registration banner + Polish NIP field are displayed.
 */

class CartDrawer {

    constructor() {

        this.drawer = document.getElementById('cart-drawer');
        this.overlay = document.getElementById('cart-drawer-overlay');
        this.container = document.getElementById('cart-drawer-items');
        this.footer = document.getElementById('cart-drawer-footer');
        this.countElements = document.querySelectorAll('.cart-count-bubble');
        this.subtotalElement = document.getElementById('cart-drawer-subtotal');

        // Bind methods
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.fetchCart = this.fetchCart.bind(this);
        this.fetchCartCount = this.fetchCartCount.bind(this);
        this.handleQuantityChange = this.handleQuantityChange.bind(this);

        // Init events
        this.initEventListeners();

    }

    initEventListeners() {

        // 2026-04-26: Cart icon click is NO LONGER intercepted - native href="/cart" works
        // Close handler retained in case drawer is opened programmatically
        document.addEventListener('click', (e) => {
            if (this.drawer && (e.target.closest('.cart-drawer__close') || e.target === this.overlay)) {
                this.close();
            }
        });

        // Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.drawer && this.drawer.classList.contains('active')) {
                this.close();
            }
        });

        // Quantity & Remove buttons inside drawer (still works if drawer is opened programmatically)
        if (this.drawer) {
            this.drawer.addEventListener('click', (e) => {
                const btn = e.target.closest('button');
                if (!btn) return;

                if (btn.classList.contains('cart-drawer__qty-btn')) {
                    const key = btn.dataset.key;
                    const action = btn.dataset.action;
                    const input = document.querySelector(`.cart-drawer__qty-input[data-key="${key}"]`);
                    let qty = parseInt(input.value);

                    if (action === 'plus') qty++;
                    if (action === 'minus') qty--;

                    this.updateItem(key, qty);
                }

                if (btn.classList.contains('cart-drawer__remove')) {
                    const key = btn.dataset.key;
                    this.updateItem(key, 0);
                }
            });

            // Input change
            this.drawer.addEventListener('change', (e) => {
                if (e.target.classList.contains('cart-drawer__qty-input')) {
                    const key = e.target.dataset.key;
                    const qty = parseInt(e.target.value);
                    this.updateItem(key, qty);
                }
            });
        }

        // Listen for custom 'cart:refresh' event (dispatched by other scripts)
        document.addEventListener('cart:refresh', this.fetchCartCount);

    }

    open() {
        if (!this.drawer) return;
        this.drawer.classList.add('active');
        this.overlay.classList.add('active');
        document.body.style.overflow = 'hidden';
        this.fetchCart();
    }

    close() {
        if (!this.drawer) return;
        this.drawer.classList.remove('active');
        this.overlay.classList.remove('active');
        document.body.style.overflow = '';
    }

    async fetchCart() {
        this.setLoading(true);
        try {
            const response = await fetch('/cart.js');
            const cart = await response.json();
            this.render(cart);
        } catch (error) {
            console.error('Error fetching cart:', error);
        } finally {
            this.setLoading(false);
        }
    }

    // 2026-04-26: lightweight refresh of just the cart-count badge - no drawer render
    async fetchCartCount() {
        try {
            const response = await fetch('/cart.js');
            const cart = await response.json();
            this.countElements.forEach(el => {
                el.textContent = cart.item_count;
                el.style.display = cart.item_count > 0 ? 'flex' : 'none';
            });
        } catch (error) {
            console.error('Error refreshing cart count:', error);
        }
    }

    async updateItem(key, quantity) {
        this.setLoading(true);
        try {
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: key, quantity: quantity })
            });
            const cart = await response.json();
            this.fetchCart();
        } catch (error) {
            console.error('Error updating item:', error);
            this.setLoading(false);
        }
    }

    // 2026-04-26: Public method for product page Add-to-cart
    // Changed: NO LONGER auto-opens drawer; shows compact toast notification instead
    async addItem(id, quantity = 1, properties = {}) {
        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: [{ id, quantity, properties }] })
            });

            if (!response.ok) throw new Error('Failed to add item');

            const data = await response.json();

            // Extract product title from response
            // Shopify /cart/add.js returns either { items: [...] } or single item object
            const item = (data.items && data.items[0]) || data;
            const productTitle = item.product_title || item.title || '';

            // Refresh cart count badge (lightweight, no drawer render)
            await this.fetchCartCount();

            // Show toast notification
            this.showAddedToast(productTitle);

        } catch (error) {
            console.error('Error adding item:', error);
            this.showAddedToast('', true); // error variant
        }
    }

    // 2026-04-26: Toast notification - bottom-right, 3s, product name + "View cart" link
    showAddedToast(productTitle, isError = false) {
        // Detect locale (PL/DE/EN) from <html lang="..."> attribute
        const lang = (document.documentElement.lang || 'en').toLowerCase();
        let locale = 'en';
        if (lang.startsWith('pl')) locale = 'pl';
        else if (lang.startsWith('de')) locale = 'de';

        const t = {
            pl: { added: 'dodano do koszyka', view_cart: 'Zobacz koszyk', error: 'Błąd dodawania do koszyka' },
            de: { added: 'zum Warenkorb hinzugefügt', view_cart: 'Warenkorb anzeigen', error: 'Fehler beim Hinzufügen' },
            en: { added: 'added to cart', view_cart: 'View cart', error: 'Error adding to cart' }
        }[locale];

        // Remove any existing toast (so subsequent adds replace cleanly)
        const existing = document.querySelector('.vk-toast');
        if (existing) existing.remove();

        const toast = document.createElement('div');
        toast.className = 'vk-toast' + (isError ? ' vk-toast--error' : '');
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');

        if (isError) {
            toast.innerHTML = `
                <div class="vk-toast__icon vk-toast__icon--error">!</div>
                <div class="vk-toast__body">
                    <div class="vk-toast__title">${t.error}</div>
                </div>
            `;
        } else {
            // Escape product title to prevent XSS
            const safeTitle = document.createElement('div');
            safeTitle.textContent = productTitle;
            const escapedTitle = safeTitle.innerHTML;

            toast.innerHTML = `
                <div class="vk-toast__icon">✓</div>
                <div class="vk-toast__body">
                    <div class="vk-toast__title">${escapedTitle} ${t.added}</div>
                    <a href="/cart" class="vk-toast__link">${t.view_cart} →</a>
                </div>
            `;
        }

        document.body.appendChild(toast);

        // 2026-04-26: Avoid overlap with cookie banner (verkline-cookie-banner)
        // Both default to bottom-right; if banner is visible we shift toast above it.
        // Works on desktop AND mobile (banner height varies but offsetHeight is accurate).
        const cookieBanner = document.querySelector('.verkline-cookie-banner.is-visible');
        if (cookieBanner && cookieBanner.offsetHeight > 0) {
            // 20px default toast gap + 20px banner gap from viewport bottom
            toast.style.bottom = (cookieBanner.offsetHeight + 40) + 'px';
        }

        // Force browser reflow then add visible class - more reliable than rAF
        // (rAF can be deferred/throttled in heavy-JS pages like product pages)
        void toast.offsetWidth;
        toast.classList.add('vk-toast--visible');

        // Auto-hide after 3 seconds (slide-out then remove)
        setTimeout(() => {
            toast.classList.remove('vk-toast--visible');
            setTimeout(() => {
                if (toast.parentNode) toast.remove();
            }, 300);
        }, 3000);
    }

    render(cart) {
        // Update Counts
        this.countElements.forEach(el => {
            el.textContent = cart.item_count;
            el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });

        if (!this.container) return;

        // Empty State
        if (cart.item_count === 0) {
            this.container.innerHTML = `
        <div class="cart-drawer__empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          <p>Twój koszyk jest pusty.</p>
          <button class="cart-drawer__continue-btn js-close-cart" onclick="document.getElementById('cart-drawer-overlay').click()">Rozpocznij zakupy</button>
        </div>
      `;
            if (this.footer) this.footer.style.display = 'none';
            return;
        }

        // Items
        let html = '';
        cart.items.forEach(item => {
            const img = item.image ? `<img src="${item.image}" alt="${item.title}">` : '';

            let priceHtml = '';
            if (item.original_price > item.final_price) {
                priceHtml = `<s>${this.formatMoney(item.original_price)}</s> <span>${this.formatMoney(item.final_price)}</span>`;
            } else {
                priceHtml = `<span>${this.formatMoney(item.final_price)}</span>`;
            }

            html += `
        <div class="cart-drawer__item" data-key="${item.key}">
          <div class="cart-drawer__item-image">
            ${img}
          </div>
          <div class="cart-drawer__item-details">
            <a href="${item.url}" class="cart-drawer__item-title">${item.product_title}</a>
            ${item.variant_title !== null && item.variant_title !== 'Default Title' ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>` : ''}
            <div class="cart-drawer__actions">
              <div class="cart-drawer__qty">
                <button type="button" class="cart-drawer__qty-btn" data-action="minus" data-key="${item.key}">-</button>
                <input type="number" class="cart-drawer__qty-input" value="${item.quantity}" min="0" data-key="${item.key}">
                <button type="button" class="cart-drawer__qty-btn" data-action="plus" data-key="${item.key}">+</button>
              </div>
              <div class="cart-drawer__item-price">${priceHtml}</div>
            </div>
            <button class="cart-drawer__remove" data-key="${item.key}">Usuń</button>
          </div>
        </div>
      `;
        });

        this.container.innerHTML = html;

        if (this.footer) {
            this.footer.style.display = 'block';
            if (this.subtotalElement) {
                this.subtotalElement.textContent = this.formatMoney(cart.total_price);
            }
        }
    }

    setLoading(isLoading) {
        if (!this.drawer) return;
        if (isLoading) {
            this.drawer.classList.add('loading');
        } else {
            this.drawer.classList.remove('loading');
        }
    }

    formatMoney(cents) {
        const shopCurrency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active ? window.Shopify.currency.active : '';
        return (cents / 100).toFixed(2).replace('.', ',') + ' ' + shopCurrency;
    }

    handleQuantityChange() {
        // Debounce if needed
    }

}

// Init
document.addEventListener('DOMContentLoaded', () => {
    window.cartDrawer = new CartDrawer();

    // Override product-page Add-to-cart form submissions (preserves AJAX, shows toast)
    // 2026-04-26: Use form.action (full URL) endsWith check instead of getAttribute
    // (Firefox may return absolute URL from getAttribute, breaking literal === comparison)
    document.addEventListener('submit', (e) => {
        const form = e.target;
        const actionAttr = form.getAttribute('action') || '';
        const actionFull = form.action || '';
        const isCartAdd = actionAttr === '/cart/add'
            || actionAttr.endsWith('/cart/add')
            || actionFull.endsWith('/cart/add');

        if (isCartAdd) {
            e.preventDefault();
            const formData = new FormData(form);
            const id = formData.get('id');
            const qty = formData.get('quantity') || 1;

            const properties = {};

            window.cartDrawer.addItem(id, qty, properties);
        }
    });
});
